import { useEffect, useRef, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { GameTitle } from '../../../components/GameTitle'
import { readError } from '../../../lib/errors'
import {
  enterOnlineQueue, fetchOnlineQueueStatus, leaveOnlineQueue,
} from '../../colorful/api/online'
import {
  fetchMyMatchmadeTokyoMetroRoom, startTokyoMetroGame, startTokyoMetroTestRoom,
} from '../api/rooms'
import { useT } from '../../../i18n'

interface Props {
  onBack: () => void
  onMatched: (roomId: string) => void
}

const POLL_INTERVAL_MS = 3000
const TARGET_PLAYERS = 4

/**
 * Tokyo Metro matchmaking waiting screen. Reuses the same queue RPCs
 * as Colorful but polls `my_matchmade_tokyo_metro_room` for the match.
 */
export function TokyoMetroOnlineMatchScreen({ onBack, onMatched }: Props) {
  const t = useT()
  const [queuedCount, setQueuedCount] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<'entering' | 'waiting' | 'matched'>('entering')
  const [bootingTestRoom, setBootingTestRoom] = useState(false)
  const matchedRef = useRef(false)

  // Solo test mode: spin up a room with the caller + 3 AI personas
  // and immediately deal the deck. Bypasses the human queue so we
  // can preview the game flow.
  async function startTestRoom() {
    if (bootingTestRoom) return
    setBootingTestRoom(true); setError(null)
    try {
      // Leave the queue if we already entered it on screen mount.
      try { await leaveOnlineQueue('tokyo_metro') } catch { /* ignore */ }
      const room = await startTokyoMetroTestRoom()
      // Deal the deck + kick off round 1.
      await startTokyoMetroGame(room.id)
      matchedRef.current = true
      onMatched(room.id)
    } catch (e) {
      setError(readError(e))
      setBootingTestRoom(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const result = await enterOnlineQueue('tokyo_metro')
        if (cancelled) return
        setQueuedCount(result.queued_count)
        if (result.matched && result.room_id) {
          matchedRef.current = true
          setStatus('matched')
          onMatched(result.room_id)
          return
        }
        setStatus('waiting')
      } catch (e) {
        if (!cancelled) {
          setError(readError(e))
          setStatus('waiting')
        }
      }
    })()
    return () => {
      cancelled = true
      if (!matchedRef.current) {
        void leaveOnlineQueue('tokyo_metro').catch(() => {})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (status !== 'waiting') return
    let cancelled = false
    const tick = async () => {
      try {
        const room = await fetchMyMatchmadeTokyoMetroRoom()
        if (cancelled) return
        if (room?.id) {
          matchedRef.current = true
          setStatus('matched')
          onMatched(room.id)
          return
        }
        const s = await fetchOnlineQueueStatus('tokyo_metro')
        if (cancelled) return
        setQueuedCount(s.queued_count)
        // Backgrounding >90s gets our queue spot swept server-side.
        // Ignoring i_am_queued left this screen spinning forever —
        // re-enter instead (idempotent upsert; may even match now).
        if (!s.i_am_queued && !matchedRef.current) {
          const back = await enterOnlineQueue('tokyo_metro')
          if (cancelled) return
          setQueuedCount(back.queued_count)
          if (back.matched && back.room_id) {
            matchedRef.current = true
            setStatus('matched')
            onMatched(back.room_id)
          }
        }
      } catch (e) {
        if (!cancelled) setError(readError(e))
      }
    }
    const id = setInterval(tick, POLL_INTERVAL_MS)
    tick()
    return () => { cancelled = true; clearInterval(id) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  async function cancel() {
    try { await leaveOnlineQueue('tokyo_metro') } catch { /* ignore */ }
    onBack()
  }

  return (
    <div className="screen">
      <PageHeader title={t('Online match')} onBack={cancel} />
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 gap-6">
        <GameTitle game="tokyo-metro" size="lg" />

        <div className="text-center">
          <div className="w-24 h-24 mx-auto rounded-full bg-brand-elevated
                          flex items-center justify-center animate-pulse-glow mb-6">
            <span className="text-4xl">🚇</span>
          </div>
          <h2 className="font-display text-2xl font-black text-brand-text mb-1">{t('Finding a match…')}</h2>
          <p className="text-brand-muted text-sm">
            You'll be slotted into the next open table of {TARGET_PLAYERS}.
          </p>
        </div>

        <div className="w-full max-w-xs">
          <div className="flex items-center justify-center gap-2 mb-3">
            {Array.from({ length: TARGET_PLAYERS }).map((_, i) => {
              const filled = i < Math.min(queuedCount, TARGET_PLAYERS)
              return (
                <div key={i}
                     className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all
                       ${filled ? 'bg-brand-text text-white border-brand-text'
                                : 'bg-white text-brand-muted border-brand-border'}`}>
                  {filled ? '👤' : '…'}
                </div>
              )
            })}
          </div>
          <p className="text-center text-sm text-brand-muted">
            {queuedCount} of {TARGET_PLAYERS} players queued
          </p>
        </div>

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 max-w-sm w-full">
            <p className="text-sm text-brand-danger text-center">{error}</p>
          </div>
        )}

        <div className="text-center text-xs text-brand-muted max-w-xs px-3">{t("Tokyo Metro is a heavy Euro. You'll take turns over hours or days. The game waits for you. Inactive seats get swapped for AI after 7 days.")}</div>
      </div>

      <div className="floating-actions space-y-2">
        <button
          onClick={startTestRoom}
          disabled={bootingTestRoom}
          className="btn-primary disabled:opacity-60"
        >
          {bootingTestRoom ? t('Starting test room…') : t('Test mode (you + 3 AI)')}
        </button>
        <button onClick={cancel} className="btn-outline">{t('Cancel matchmaking')}</button>
      </div>
    </div>
  )
}
